/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2021, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([], () => {
	/**
	 * This class provides functions that aims to replace the same or similar functions provided by underscore
	 * It satisfies our own needs and we are making them as much consistent as possible with underscore
	 */
	class CommonUtil {
		/**
		 *@description
		 *	Returns object after filling in its undefined properties with the value present in the following list of defaults objects
		 *@param { object } source object
		 *@param { defaultValues } list of objects as arguments, separated by comma
		 *@return { object } Object after filling in its undefined properties
		 @example
		 	the result of defaults({a:1}, {a:2, b:1}, {b:2, c:1}) is {a:1, b:1, c:1}
		 */
		static defaults(object, ...defaultValues){
			//consistent with underscore.defaults(), for now, no error handling for
			//undefined or null object
			if (typeof defaultValues !== 'object' || !defaultValues) {
				return object;
			}
			const reducer = (accumulator, currentValue) => {
				if(!currentValue){
					return accumulator;
				}
				for (const prop of Object.getOwnPropertyNames(currentValue)) {
					if (!accumulator.hasOwnProperty(prop)) {
						accumulator[prop] = currentValue[prop];
					}
				}
				return accumulator;
			};

			return defaultValues.reduce(reducer, object);
		}

		/**
		 *@description
		 *	Returns true if value is an Object. Note that JavaScript arrays and functions are objects, while (normal) strings and numbers are not.
		 *@param { * } target
		 *@return { boolean }
		 */
		static isObject(obj) {
			return typeof obj === 'function' || typeof obj === 'object' && obj !== null;
		}

		/**
		 *@description
		 *	Returns true if the object has no enumerable own-properties. Note that primitive numbers, booleans
		 *  and symbols are always empty by this definition.
		 *@param { object } target
		 *@return { boolean }
		 */
		static isEmpty(obj) {
			if (obj === undefined || obj === null) {
				return true;
			}
			return Object.keys(obj).length === 0;
		}

		/**
		 *@description
		 *	Produces a duplicate-free version of the array, using === to test object equality.
		 *	In particular only the first occurrence of each value is kept
		 *@param {Array} An array that could have duplicated members
		 *@return {Array} An array with unique members
		 */
		static uniq(arr) {
			if (Array.isArray(arr)) {
				return Array.from(new Set(arr));
			}
			return arr;
		}

		/**
		 *@description
		 *	Produces a duplicate-free version of the array, compare the value of the given property of each member to test object equality.
		 *	In particular only the first occurrence of each value is kept
		 *@param { Array } An array that could have duplicated members checked by the given property
		 *@param { string } property name
		 *@return { Array } An array with unique members
		 *@example
		 *	The result of:

		 	uniqBy(
			 [{ key: 'key1', value: 'value1' },
				{ key: 'key2', value: 'value1' },
				{ key: 'key3', value: 'value2' },
				{ key: 'key3', value: 'value2' },
				{ key: 'key4', value: 'value3' }
		 	], 'value')

			is:

			[
				{ key: 'key1', value: 'value1' },
				{ key: 'key3', value: 'value2' },
				{ key: 'key4', value: 'value3' }
			]

			Note: if 'prop' is not the array entry's known property or the property is invalid such as undefined or null,
			the result of the function will be an array that contains the first element of the original array, which is
			consistent with underscore uniq
		 */
		static uniqBy(arr, prop) {
			if(!Array.isArray(arr)){
				//TODO: evaluate 'throw error' .vs. 'output original input'
				return arr;
			}
			const result = [];
			const map = new Map();
			for (const item of arr) {
				if (!map.has(item[prop])) {
					map.set(item[prop], true);
					result.push(item);
				}
			}
			return result;
		}

		/**
		 *@description
		 *	Return all of the values of the object's own properties.
		 *@param { object } target
		 *@return { Array }
		 */
		static values(obj) {
			if (obj === undefined || obj === null) {
				return [];
			}
			return Object.values(obj);
		}

		/**
		 *@description
		 *	Return a (shallow-cloned) duplicate of an object.
		 *@param { object } obj source object
		 *@return { object } shallow-cloned object
		 */
		static clone(obj) {
			if (Array.isArray(obj)) {
				return obj.slice(0);
			}
			return Object.assign({}, obj);
		}

		/**
		 * @description Deep clone an object
		 * @param {Object} obj Source object to clone
		 * @returns cloned object
		 */
		static deepClone(obj) {
			return JSON.parse(JSON.stringify(obj));
		}

		/**
		 * @description Deep copy all the properties in the source object over with the desination object properties.
		 * Properties can either be copied directly to the destination object or on to a cloned object without mutating
		 * the destination object.
		 * @param {Object} destination
		 * @param {Object} source
		 * @param {boolean} [clone = false] if false (default) copy the properties of source object to the destination object,
		 * otherwise copy the properties to a cloned object without mutating the destination object.
		 * @returns extended object with properties of the destination and source object
		 */
		static deepExtend(destination, source, clone = false) {
			const target = clone && destination ? CommonUtil.deepClone(destination) : destination || {};
			// Walk the source object properties with keys, including indice (if property is an Array)
			Object.keys(source || {}).forEach(key => {
				const type = typeof source[key];
				if (type === 'object') {
					if (target[key]) {
						// merge extend with the existing target
						CommonUtil.deepExtend(target[key], source[key]);
					} else {
						// simply clone the source to target
						target[key] = CommonUtil.deepClone(source[key]);
					}
				} else {
					// extend with primitive values and functions
					target[key] = source[key];
				}
			});
			return target;
		}

		/**
		 * A sort compare function based on position (y > x).
		 * Undefined/null values will be sorted last.
		 * @param {Number} [threshold = 0] Threshold to equate to similar numbers
		 * If the difference in 2 coordinate values are less than or equal to the threshold, they would be considered equal
		 * @returns The comparator function to use for an array sort
		 */
		static positionComparator(threshold = 0) {
			const compare = (a, b) => {
				if (a == null && b == null)
					return 0;
				else if (a == null)
					return 1;
				else if (b == null)
					return -1;
				else {
					const difference = a - b;
					return Math.abs(difference) <= threshold ? 0 : difference;
				}
			};
			return (positionA, positionB) => {
				const yCompare = compare(positionA.y, positionB.y);
				return (yCompare === 0) ? compare(positionA.x, positionB.x) : yCompare;
			};
		}
	}

	return CommonUtil;
});
